import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useDynamicPrompts, DynamicPromptConfig, PromptCache } from '../hooks/useDynamicPrompts';

interface PromptContextType {
  // State
  promptCache: PromptCache;
  
  // Actions
  getPromptForProject: (assetType: string, projectId?: string, context?: Record<string, any>) => Promise<DynamicPromptConfig>;
  getPromptForAsset: (assetType: string, projectId?: string, context?: Record<string, any>) => Promise<DynamicPromptConfig>;
  clearCache: () => void;
  refreshPrompt: (assetType: string, projectId?: string, context?: Record<string, any>) => Promise<DynamicPromptConfig>;
}

const PromptContext = createContext<PromptContextType | undefined>(undefined);

interface PromptProviderProps {
  children: ReactNode;
}

export const PromptProvider: React.FC<PromptProviderProps> = ({ children }) => {
  const promptHook = useDynamicPrompts();

  return (
    <PromptContext.Provider value={promptHook}>
      {children}
    </PromptContext.Provider>
  );
};

export const usePromptContext = (): PromptContextType => {
  const context = useContext(PromptContext);
  if (context === undefined) {
    throw new Error('usePromptContext must be used within a PromptProvider');
  }
  return context;
};

// Convenience hooks for specific use cases
export const useProjectPrompt = (assetType: string, projectId?: string) => {
  const { getPromptForProject, promptCache } = usePromptContext();
  const cacheKey = `${assetType}-${projectId || 'default'}`;
  
  const loadPrompt = useCallback(() => {
    console.log(`🔄 Loading prompt for ${assetType}${projectId ? ` (project: ${projectId})` : ' (default)'}`);
    return getPromptForProject(assetType, projectId);
  }, [getPromptForProject, assetType, projectId]);
  
  return {
    prompt: promptCache[cacheKey],
    loadPrompt,
    isLoading: promptCache[cacheKey]?.isLoading || false,
    error: promptCache[cacheKey]?.error || null
  };
};

export const useAssetPrompt = (assetType: string, projectId?: string) => {
  const { getPromptForAsset, promptCache } = usePromptContext();
  const cacheKey = `${assetType}-${projectId || 'default'}`;
  
  const loadPrompt = useCallback(() => {
    return getPromptForAsset(assetType, projectId);
  }, [getPromptForAsset, assetType, projectId]);
  
  return {
    prompt: promptCache[cacheKey],
    loadPrompt,
    isLoading: promptCache[cacheKey]?.isLoading || false,
    error: promptCache[cacheKey]?.error || null
  };
}; 